import { queryClient } from '../api/network/queryClient';
import { getFaqQueryConfig } from '../api/queries/faq';
import { isTicketCancellable, isTicketReschedulable } from '../helpers/TicketHelpers';
import { FAQArticle, FAQCategory } from '../interfaces/FAQ';
import { Stylesheet } from '../interfaces/Stylesheet';
import { Ticket } from '../interfaces/Ticket';

type Translations = {
  thisUrl: string;
};

const prefetchFAQ = (hashId: string) => {
  const params = getFaqQueryConfig(hashId);
  queryClient.prefetchQuery(params);
};

const escapeChars = (text: string): string => {
  return text.replace(/[^a-z0-9-'\s]/g, '');
};

const highlightText = (text: string, search: string | undefined): string => {
  let t = text;

  if (text && search) {
    t = text.replace(
      new RegExp('(?![^\[]*]|[^<]*>)(' + escapeChars(search) + ')', 'gi'),
      '<span class="highlighted">$1</span>'
    );
  }

  return t;
};

interface FilterFAQsResult {
  faqs: FAQArticle[];
  searchTexts: string[];
}

const filterFAQs = (faqs: FAQArticle[], search: string): FilterFAQsResult => {
  if (!search.trim()) {
    return {
      faqs,
      searchTexts: []
    };
  }

  const searchTexts = escapeChars(search.toLowerCase())
    .split(' ')
    .filter(text => !!text); // make sure '' does not count as matching all results

  return {
    faqs: faqs.filter(faq => {
      return [faq.articleBody, faq.title].some(text => {
        const textLower = text.toLowerCase();

        return searchTexts.some(searchText => textLower.includes(searchText));
      });
    }),
    searchTexts
  };
};

const replaceKnowledgeFields = (
  text: string,
  ticket: Ticket | undefined,
  stylesheet: Stylesheet,
  translations: Translations,
) => {
  const partnerName = stylesheet.settings.partnerName;
  const partnerPhoneNumber = stylesheet.contact.partnerPhoneNumber;

  const cancelUrl = `${window.location.protocol}//${window.location.host}/${ticket?.hashId || 'not-found'}/cancel`;
  const changeUrl = `${window.location.protocol}//${window.location.host}/${ticket?.hashId || 'not-found'}/change`;

  return text.replace('%partner_name%', partnerName)
    .replace('%partner_phonenumber%', `<a href="tel:${partnerPhoneNumber}">${partnerPhoneNumber}</a>`)
    .replace('%cancel_url%', `<a href="${cancelUrl}">${translations.thisUrl}</a>`)
    .replace('%change_url%', `<a href="${changeUrl}">${translations.thisUrl}</a>`);
};

const wrapInParagraph = (text: string) => {
  if (!text.includes('<p>')) {
    return `<p>${text}</p>`;
  }

  return text;
};

const replaceMergeFields = (
  faq: FAQCategory[],
  ticket: Ticket,
  stylesheet: Stylesheet,
  translations: Translations,
): FAQCategory[] => {
  const canCancel = isTicketCancellable(ticket, stylesheet);
  const canReschedule = isTicketReschedulable(ticket);

  return faq.map((section) => ({
    ...section,
    articles: section.articles
      .filter((article) => {
        const cancelConditionPassed = !article.articleBody.includes('%cancel_url%') || canCancel;
        const rescheduleConditionPassed = !article.articleBody.includes('%change_url%') || canReschedule;

        return cancelConditionPassed && rescheduleConditionPassed;
      })
      .map((article) => ({
        ...article,
        articleBody: replaceKnowledgeFields(
          article.articleBody, ticket, stylesheet, translations),
      })),
  })).filter((section) => section.articles.length > 0);
};

export default {
  highlightText,
  filterFAQs,
  escapeChars,
  replaceKnowledgeFields,
  prefetchFAQ,
  wrapInParagraph,
  replaceMergeFields
};
